/**
 * Funções utilitárias para limpeza de strings JSON
 */
const cleanJsonString = (
  input: string,
  level: 'normal' | 'aggressive' | 'lastchance' = 'normal',
): string => {
  switch (level) {
    case 'normal':
      return input
        .replace(/\\n/g, '')
        .replace(/\\"/g, '"')
        .replace(/\\r/g, '')
        .replace(/\\t/g, '')
        .replace(/\\\\/g, '\\');

    case 'aggressive':
      return input
        .replace(/\\/g, '')
        .replace(/\n/g, '')
        .replace(/\r/g, '')
        .replace(/\t/g, '');

    case 'lastchance':
      return input
        .replace(/,\s*([}\]])/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
  }
};

/**
 * Função base para parsing de JSON com múltiplas tentativas de limpeza
 */
const parseJsonWithRetries = <T>(input: string): T | null => {
  try {
    return JSON.parse(input);
  } catch (error) {
    try {
      return JSON.parse(cleanJsonString(input, 'normal'));
    } catch (error) {
      try {
        return JSON.parse(cleanJsonString(input, 'aggressive'));
      } catch (error) {
        try {
          return JSON.parse(cleanJsonString(input, 'lastchance'));
        } catch (error) {
          return null;
        }
      }
    }
  }
};

/**
 * Função para normalizar strings JSON que devem retornar um array
 * @param input - String ou objeto a ser normalizado
 * @returns Array normalizado
 */
export function normalizeStringJsonArray<T = any>(
  input: string | object | undefined | null,
): T[] {
  // Verifica se o input é nulo ou undefined
  if (!input) {
    return [];
  }

  // Se já for um array, retorna diretamente
  if (Array.isArray(input)) {
    return input as T[];
  }

  // Se for um objeto não-arrays, avisa e retorna array vazio
  if (typeof input === 'object') {
    console.warn('⚠️ Use normalizeStringJsonObject para objetos.', input);

    return [];
  }

  // Tenta fazer o parsing do JSON
  const parsedArray = parseJsonWithRetries<T[]>(input as string);

  if (!parsedArray) {
    try {
      return (input as string).split(',') as T[];
    } catch (error) {
      console.warn('⚠️ Falha ao normalizar string JSON para array:', input);

      return [];
    }
  }

  return parsedArray;
}

/**
 * Função para normalizar strings JSON que devem retornar um objeto
 * @param input - String ou objeto a ser normalizado
 * @returns Objeto normalizado
 */
export function normalizeStringJsonObject<T = Record<string, any>>(
  input: string | object | undefined | null,
): T {
  // Verifica se o input é nulo ou undefined
  if (!input) {
    return {} as T;
  }

  // Se já for um objeto não-arrays, retorna diretamente
  if (typeof input === 'object' && !Array.isArray(input)) {
    return input as T;
  }

  // Se for um array, avisa e retorna objeto vazio
  if (Array.isArray(input)) {
    console.warn('⚠️ Use normalizeStringJsonArray para arrays.', input);

    return {} as T;
  }

  // Tenta fazer o parsing do JSON
  const parsedObject = parseJsonWithRetries<T>(input as string);

  if (!parsedObject) {
    console.warn('⚠️ Falha ao normalizar string JSON para objeto:', input);

    return {} as T;
  }

  return parsedObject;
}
